import PropTypes from 'prop-types';
import { Controller, useFieldArray } from 'react-hook-form';
import { cn } from '#/lib/utils';

import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import {
  createEmptyAddressRow,
} from '#/modules/momMgmt/settings/processingFactory/helper/mapProcessingFactoryForm';

const ProcessingFactoryAddressesEditor = ({ control, register, errors, setValue }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'addresses',
  });

  function handleAddAddress() {
    append(createEmptyAddressRow());
  }

  function handleDefaultChange(index, checked) {
    setValue(`addresses.${index}.isDefault`, Boolean(checked), {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (!checked) {
      return;
    }

    fields.forEach((_, otherIndex) => {
      if (otherIndex === index) {
        return;
      }
      setValue(`addresses.${otherIndex}.isDefault`, false, {
        shouldDirty: true,
      });
    });
  }

  return (
    <div className="form-section @container/form-section">
      <div className="form-section__header">
        <h2 className="form-block__title mb-0!">地址</h2>
        <Button type="button" size="sm" onClick={handleAddAddress}>
          新增地址
        </Button>
      </div>

      {fields.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          尚未新增地址；儲存時將以空陣列清空（replace-all）。
        </p>
      ) : null}

      {errors?.addresses?.message ? (
        <p className="mt-3 text-sm text-red-500">{errors.addresses.message}</p>
      ) : null}

      {fields.length > 0 ? (
        <div
          className={cn(
            'form-section__body',
            'grid grid-cols-1 gap-3',
            '@lg/form-section:grid-cols-2',
          )}
        >
          {fields.map((field, idx) => (
            <div
              key={field.id}
              className={cn(
                'px-3 py-5',
                'bg-gray-50',
                'border border-gray-200 rounded-md',
              )}
            >
              <FloatingInput
                id={`addresses.${idx}.addressType`}
                label={`類型 #${idx + 1}`}
                register={register}
                errors={errors}
              />
              <div className="grid grid-cols-1 gap-0 sm:grid-cols-3 sm:gap-2">
                <FloatingInput
                  id={`addresses.${idx}.postalCode`}
                  label="郵遞區號"
                  register={register}
                  errors={errors}
                />
                <FloatingInput
                  id={`addresses.${idx}.city`}
                  label="縣市"
                  register={register}
                  errors={errors}
                />
                <FloatingInput
                  id={`addresses.${idx}.district`}
                  label="行政區"
                  register={register}
                  errors={errors}
                />
              </div>
              <FloatingInput
                id={`addresses.${idx}.address`}
                label="地址"
                register={register}
                errors={errors}
              />
              <FloatingInput
                id={`addresses.${idx}.remark`}
                label="備註"
                register={register}
                errors={errors}
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Controller
                  name={`addresses.${idx}.isDefault`}
                  control={control}
                  render={({ field: checkboxField }) => (
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      預設地址
                      <Checkbox
                        checked={Boolean(checkboxField.value)}
                        onCheckedChange={(checked) => handleDefaultChange(idx, checked)}
                      />
                    </label>
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(idx)}
                >
                  移除項目
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

ProcessingFactoryAddressesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
};

export default ProcessingFactoryAddressesEditor;
